
<template>
  <on-click-outside @outside="clickOutside" class="">
    <div class="search-select">

      <div :class="[
          input ? 'search-select-input-layout-data' : 'search-select-input-layout-empty',
          bg === 'light' ? 'bg-light' : 'bg-middle'
        ]">
        <input ref="input" class="text-input bg-input-button"
          :placeholder="placeholder"
          v-model="inputModel"
          @focus="hasFocus = true"
          @blur="hasFocus = false"
          @keydown.esc="inputEsc"
          @keydown.up="inputUp"
          @keydown.down="inputDown"
          @keydown.enter.prevent="inputEnter"
          @keydown.tab.prevent
        >
        <button v-if="input" @click="resetButton" class="input-icon-button bg-input-button">
          <font-awesome-icon icon="times" />
        </button>
      </div>

      <div ref="dropdown" v-if="isOpen" class="search-select-dropdown ">
        <content-spinner v-if="loading" class="ma2 center" />
        <ul ref="options" class="search-select-options">
          <li v-for="(option, index) in options" :key="index" @click="optionClicked(index)"
            class="search-select-option truncate f6 dim"
            :class="[index === highlightedIndex ? 'is-active' : '']"
          >
            {{ labelProvider(option) }}
          </li>
        </ul>
        <div v-if="notFoundMessage" class="search-select-empty f6">
          {{ notFoundMessage }}
        </div>
      </div>

    </div>
  </on-click-outside>
</template>

<script>
import OnClickOutside from './OnClickOutside.vue'
import ContentSpinner from '@/components/common/ContentSpinner.vue'

export default {
  components: {
    OnClickOutside,
    ContentSpinner
  },
  props: {
    searchFunction: {
      default: (value) => {
        return Promise.resolve({ items: [] })
      }
    },
    labelProvider: {
      default: () => {
        return (v) => {
          return v ? v.name : v
        }
      }
    },
    placeholder: { default: null },
    bg: { default: 'light' }
  },
  computed: {
    inputModel: {
      get () {
        return this.input
      },
      set (v) {
        this.input = v
        this.search(v)
      }
    },
    hasOptions () {
      return Array.isArray(this.options) && this.options.length > 0
    },
    isOpen () {
      return this.hasFocus
        ? this.input
          ? (this.loading || this.hasOptions || this.notFoundMessage)
          : false
        : this.hasOptions || this.loading
    }
  },
  data () {
    return {
      hasFocus: false,
      debounceSearch: this.$lo.debounce(this.runSearchFunction, 1000),
      loading: false,
      options: [],
      notFoundMessage: null,
      highlightedIndex: 0,
      input: null
    }
  },
  methods: {
    inputEnter () {
      if (this.isOpen) {
        if (this.hasOptions) this.select(this.highlightedIndex)
      } else {
        if (this.input) this.search(this.input)
      }
    },
    inputUp () {
      if (this.isOpen && this.hasOptions) {
        this.highlight(this.highlightedIndex - 1)
      }
    },
    inputDown () {
      if (this.isOpen && this.hasOptions) {
        this.highlight(this.highlightedIndex + 1)
      }
    },
    inputEsc () {
      this.cancelSearch()
    },
    clickOutside () {
      this.cancelSearch()
    },
    resetButton () {
      this.reset()
    },
    optionClicked (index) {
      this.select(index)
    },

    search (input) {
      this.options = []
      this.notFoundMessage = null

      if (input) {
        this.loading = true
        this.debounceSearch(input)
      } else {
        this.cancelSearch()
      }
    },
    runSearchFunction (value) {
      this.searchFunction(value)
        .then(({ items }) => {
          if (this.loading) {
            this.highlightedIndex = 0
            if (Array.isArray(items) && items.length > 0) {
              this.options = items
            } else {
              this.notFoundMessage = 'По вашему запросу ничего не найдено'
            }
          }
        })
        .finally(() => {
          this.loading = false
          this.scrollToHighlighted()
        })
    },
    cancelSearch () {
      this.debounceSearch.cancel()
      this.loading = false
    },

    reset () {
      this.options = []
      this.input = null
      this.notFoundMessage = null
      this.highlightedIndex = 0
      this.$refs.input.focus()
    },

    select (index) {
      this.$emit('selected', this.options[index])
      this.options = []
      this.notFoundMessage = null
      this.highlightedIndex = 0
      this.$refs.input.focus()
    },

    highlight (index) {
      this.highlightedIndex = index

      if (this.highlightedIndex < 0) {
        this.highlightedIndex = this.options.length - 1
      }

      if (this.highlightedIndex > this.options.length - 1) {
        this.highlightedIndex = 0
      }

      this.scrollToHighlighted()
    },

    scrollToHighlighted () {
      const highlighted = this.$refs.options?.children[this.highlightedIndex]
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.search-select {
  position: relative;
}
.search-select-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.search-select-input-layout-empty {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 180;
  justify-items: start;
  align-items: center;
}
.search-select-input-layout-data {
  display: grid;
  grid-template-columns: 1fr 40px;
  grid-template-rows: 180;
  justify-items: start;
  align-items: center;
}
.search-select-input {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  min-height: 40px;
  background-color: inherit;
  padding: 0 0;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  outline: none;
  border: none;
  -webkit-tap-highlight-color: rgba(0,0,0,0);
  text-align: left;
  padding: .5rem 1rem;
}
.search-select-input:focus {
  outline: 0;
  border-color: #627D98;
}
.search-select-placeholder {
  color: #8795a1;
}
.search-select.is-active .search-select-input {
  -webkit-box-shadow: 0 0 0 3px rgba(52, 144, 220, 0.5);
  box-shadow: 0 0 0 3px rgba(52, 144, 220, 0.5);
}
.search-select-dropdown {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  position: absolute;
  right: 0;
  left: 0;
  background-color: white;
  padding: 0;
  -webkit-box-shadow: 0 4px 4px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 4px 4px 2px rgba(0, 0, 0, 0.2);
  z-index: 50;
}
.search-select-options {
  list-style: none;
  padding: 0;
  margin: 0;
  position: relative;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  max-height: 14rem;
}
.search-select-option {
  margin: 0;
  padding: 0.8rem 0.5rem;
  color: #334E68;
  border-left: 0;
  border-right: 0;
  border-top: 0px;
  border-bottom: 1px;
  border-style: solid;
  border-color: #eeeeee;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.search-select-option:hover {
  background-color: #F4F4F4;
}
.search-select-option.is-active,
.search-select-option.is-active:hover {
  background-color: #F0F4F8;
  color: #334E68;
}
.search-select-empty {
  padding: 0.5rem 1rem;
  color: #b8c2cc;
}
.bg-input-button {
  background-color: inherit;
}
</style>
